import { useEffect, useRef } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { TooltipProvider } from "./ui/tooltip";

import { Sidebar } from "./Sidebar";
import { useAuth } from "../app/AuthContext";
import {
  SidebarInset,
  SidebarProvider,
  useSidebar as useSidebarPrimitive,
} from "./ui/sidebar";

export function useSidebar() {
  const sidebar = useSidebarPrimitive();
  return {
    open: sidebar.isMobile ? sidebar.openMobile : sidebar.open,
    toggle: sidebar.toggleSidebar,
    close: () => sidebar.setOpenMobile(false),
  };
}

function AuthenticatedShell({ children }: { children?: React.ReactNode }) {
  return (
    <>
      <Sidebar />
      <SidebarInset
        id="route-host"
        className="h-svh overflow-hidden bg-background [&>*]:min-h-0"
      >
        {children ?? <Outlet />}
      </SidebarInset>
    </>
  );
}

function ShellGuard({ children }: { children?: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasToken } = useAuth();
  const redirected = useRef(false);

  useEffect(() => {
    if (!hasToken && location.pathname !== "/login" && !redirected.current) {
      redirected.current = true;
      navigate("/login", { replace: true });
      return;
    }
    if (
      hasToken &&
      location.pathname === "/login" &&
      !redirected.current
    ) {
      redirected.current = true;
      navigate("/", { replace: true });
      return;
    }
    if (hasToken) redirected.current = false;
  }, [hasToken, location.pathname, navigate]);

  if (!hasToken) return <>{children ?? <Outlet />}</>;
  return <AuthenticatedShell>{children}</AuthenticatedShell>;
}

export function AppShell({ children }: { children?: React.ReactNode }) {
  return (
    <TooltipProvider delay={120}>
      <SidebarProvider>
        <ShellGuard>{children}</ShellGuard>
      </SidebarProvider>
    </TooltipProvider>
  );
}
